import { useCallback, useRef } from 'react';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import {
	CONTEXTUAL_INVITE_NUDGE,
	LIVE_PAGE_NUDGE,
} from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import {
	useGetOnboardingState,
	useSetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { UNSAFE_expValNoExposure } from '@confluence/feature-experiments';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';

import { useShareNudgeSpotlightActions } from '../store/useShareNudgeSpotlightStore.tsx';

interface ShareNudgeEligible {
	isEligibleForShareNudge: boolean;
	isLoading: boolean;
	showShareNudgeSpotlight: () => void;
	setLiveDocsNudgeEligible: () => void;
	hasSeenSpotlight?: () => void;
}

/**
 * Determines eligibility for the Share Nudge Spotlight
 *
 * This hook handles the share nudge spotlight functionality for live pages.
 * The nudge is triggered when new founding admins are on a live page.
 *
 * **IMPORTANT REQUIREMENTS:**
 * - User must be a Founding Admin
 * - User must have admin or use access
 * - User must not have seen the live page nudge before
 *
 * The `showShareNudgeSpotlight` function must be called when content changes
 * occur on live pages to trigger the nudge. This is typically integrated at the editor level
 * to detect when users make their first content changes.
 *
 * @returns {object} - an object containing:
 * @returns {boolean} isEligibleForShareNudge - Whether the user is eligible for the share nudge (Founding Admin)
 * @returns {boolean} isLoading - Whether queries are still loading
 * @returns {function} showShareNudgeSpotlight - Call this when content changes to show the share spotlight nudge
 * @returns {function} setLiveDocsNudgeEligible - Call this to set onboarding state for live docs nudge eligibility
 */
export const useShareNudgeEligible = (): ShareNudgeEligible => {
	const { setShouldShowSpotlight } = useShareNudgeSpotlightActions();
	const { accessStatus } = useSessionData();

	const { setOnboardingState } = useSetOnboardingState();

	const hasAdminOrUseAccess =
		accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS ||
		accessStatus === AccessStatus.LICENSED_USE_ACCESS;

	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState(
		[...Object.values(CONTEXTUAL_INVITE_NUDGE), ...Object.values(LIVE_PAGE_NUDGE)],
		//Skip checking onboarding state if not-enrolled in treatment
		// eslint-disable-next-line confluence-feature-gating/no-exposure-recommendation -- Exposed when landing in Onboarding Screens
		!UNSAFE_expValNoExposure('cfind-4521-live-docs-spotlight-nudge', 'enrolled', false) ||
			!hasAdminOrUseAccess,
	);

	// User Eligibility Check
	const onboardingState = deserializeState(onboardingStateData);
	const { wasLivePageNudgeSeen, shareNudgeSpotlightEligible } = onboardingState;

	const isLoading = !!onboardingStateLoading;
	const error = onboardingStateError;

	if (error) {
		if (isUnauthorizedError(error)) {
			markErrorAsHandled(error);
		}
	}

	const isEligibleForShareNudge = !isLoading && !error && shareNudgeSpotlightEligible;

	const hasSeenSpotlight = () => {
		void setOnboardingState({
			key: LIVE_PAGE_NUDGE.LIVE_PAGE_CONTEXTUAL_NUDGE_SEEN,
			value: 'true',
		});
	};

	const setLiveDocsNudgeEligible = useCallback(() => {
		void setOnboardingState({
			key: LIVE_PAGE_NUDGE.LIVE_PAGE_CONTEXTUAL_NUDGE_ELIGIBLE,
			value: 'true',
		});
	}, [setOnboardingState]);

	const draftSyncCallCount = useRef(0);

	const showShareNudgeSpotlight = useCallback(() => {
		if (isEligibleForShareNudge && !wasLivePageNudgeSeen) {
			draftSyncCallCount.current++;
			// Only show spotlight after 2nd call (skip immediate "on load" call)
			if (draftSyncCallCount.current > 1) {
				setShouldShowSpotlight(true);
			}
		}
	}, [isEligibleForShareNudge, wasLivePageNudgeSeen, setShouldShowSpotlight]);

	return {
		isLoading,
		isEligibleForShareNudge,
		hasSeenSpotlight,
		setLiveDocsNudgeEligible,
		showShareNudgeSpotlight,
	};
};
